import {Fragment, useContext, useEffect, useMemo, useState, useRef} from "react";
import {useQuery} from "react-query";
import {toast} from "react-toastify";

import AuthContext from "../../context/AuthContext";
import {fetcher, hasLoginError} from "../../functions/requestFetcher";
import {
  COUNTRY_OPTIONS,
  LANGUAGE_OPTIONS,
  getRegionalPreferences,
} from "../../functions/regionPreferences";
import {t} from "../../functions/i18n";

import Input from "../../components/UI/Input/Input";
import Button from "../../components/UI/Button/Button";
import Spinner from "../../components/UI/Loading/Spinner";

import styles from "./Settings.module.scss";

function Settings({token, logout, rootElementRef}) {
  const {username, avatarUrl, language, country, editAuthConf} =
    useContext(AuthContext);

  const detectedRegionalPreferences = useMemo(
    () => getRegionalPreferences({language, country}),
    [language, country]
  );

  const [profileForm, setProfileForm] = useState({
    name: username || "",
    avatarUrl: avatarUrl || "",
    language: detectedRegionalPreferences.language,
    country: detectedRegionalPreferences.country,
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const avatarInputRef = useRef(null);

  const triggerAvatarUpload = () => {
    avatarInputRef.current?.click();
  };

  useEffect(() => {
    rootElementRef?.current &&
      rootElementRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  }, [rootElementRef]);

  const {isLoading} = useQuery(
    ["auth", "profile", token],
    () =>
      fetcher(
        {
          url: window.__SOUNDREX_API_URL__ + "/auth/profile",
          method: "GET",
          headers: {
            authorization: "Bearer " + token,
          },
        },
        false,
        false,
        false,
        null,
        null
      ),
    {
      enabled: Boolean(token),
      onSuccess: data => {
        const profile = data?.profile;
        if (!profile) return;

        editAuthConf({
          username: profile.username,
          avatarUrl: profile.avatarUrl || "",
          language: profile.language,
          country: profile.country,
        });

        setProfileForm({
          name: profile.username || "",
          avatarUrl: profile.avatarUrl || "",
          language: profile.language || detectedRegionalPreferences.language,
          country: profile.country || detectedRegionalPreferences.country,
        });
      },
      onError: error => {
        if (hasLoginError(error)) {
          logout();
        }
      },
    }
  );

  const updateProfileField = event => {
    const {name, value} = event.target;
    setProfileForm(prev => ({...prev, [name]: value}));
  };

  const updatePasswordField = event => {
    const {name, value} = event.target;
    setPasswordForm(prev => ({...prev, [name]: value}));
  };

  const applyDetectedPreferences = () => {
    setProfileForm(prev => ({
      ...prev,
      language: detectedRegionalPreferences.language,
      country: detectedRegionalPreferences.country,
    }));
  };

const handleAvatarUpload = async event => {
    const file = event.target.files[0];
    if (!file || !token) return;

    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const response = await fetch(window.__SOUNDREX_API_URL__ + "/auth/avatar", {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + token,
        },
        body: formData,
      });
      
      const data = await response.json();

      if (data?.avatarUrl) {
        setProfileForm(prev => ({ ...prev, avatarUrl: data.avatarUrl }));
        editAuthConf({ avatarUrl: data.avatarUrl });
        toast.success(t(language, "profileImageUpdated") || "Profile image updated");
      }
    } catch (error) {
      toast.error(t(language, "profileImageError") || "Error uploading image");
    }
  };

  const submitProfile = async event => {
    event.preventDefault();
    setIsSavingProfile(true);

    try {
      const data = await fetcher(
        {
          url: window.__SOUNDREX_API_URL__ + "/auth/profile",
          method: "PATCH",
          headers: {
            authorization: "Bearer " + token,
          },
          data: profileForm,
        },
        false,
        false,
        false,
        null,
        null
      );

      const profile = data?.profile;
      if (profile) {
        editAuthConf({
          username: profile.username,
          avatarUrl: profile.avatarUrl || "",
          language: profile.language,
          country: profile.country,
        });
      }

      toast.success("Settings saved successfully.", {
        autoClose: 1500,
        theme: "colored",
      });
    } catch (error) {
      if (hasLoginError(error)) {
        logout();
      }

      toast.error(
        error?.response?.data?.message || "Unable to save profile settings.",
        {
          autoClose: 1800,
          theme: "colored",
        }
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  const submitPassword = async event => {
    event.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("The new passwords do not match.", {
        autoClose: 1800,
        theme: "colored",
      });
      return;
    }

    setIsSavingPassword(true);

    try {
      await fetcher(
        {
          url: window.__SOUNDREX_API_URL__ + "/auth/password",
          method: "PATCH",
          headers: {
            authorization: "Bearer " + token,
          },
          data: {
            currentPassword: passwordForm.currentPassword,
            newPassword: passwordForm.newPassword,
          },
        },
        false,
        false,
        false,
        null,
        null
      );

      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      toast.success("Password updated successfully.", {
        autoClose: 1500,
        theme: "colored",
      });
    } catch (error) {
      if (hasLoginError(error)) {
        logout();
      }

      toast.error(
        error?.response?.data?.message || "Unable to update password.",
        {
          autoClose: 1800,
          theme: "colored",
        }
      );
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <div className={styles.settings}>
        <div className={styles["settings__header"]}>
        <h1 className="display-medium white">{t(language, "settingsTitle")}</h1>
        <p className="text-medium gray">
          {t(language, "settingsSubtitle")}
        </p>
      </div>

      {isLoading ? (
        <div className="flex-center mg-t-3">
          <Spinner />
        </div>
      ) : (
        <Fragment>
          <form className={styles["settings__section"]} onSubmit={submitProfile}>
            <h2 className="display-small white mg-b-3">{t(language, "profile")}</h2>
            
            <div className={styles["settings__avatar-container"]}>
              <img
                className={styles["settings__avatar"]}
                src={profileForm.avatarUrl || "/icons/maskable_icon_x192.png"}
                alt={t(language, "profile")}
                onClick={triggerAvatarUpload}
              />
              <div className={styles["settings__avatar-overlay"]}>
                <span className="material-icons">edit</span>
              </div>
              <div 
                className={styles["settings__avatar-badge"]}
                onClick={triggerAvatarUpload}
              >
                +
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className={styles["settings__avatar-file"]}
                onChange={handleAvatarUpload}
              />
            </div>

            <Input
              required={true}
              id="settings-name"
              label={t(language, "displayName")}
              placeholder={t(language, "publicName")}
              type="text"
              control="input"
              value={profileForm.name}
              onChange={updateProfileField}
              name="name"
            />

            

            <div className={styles["settings__row"]}>
              <div>
                <label className="link-medium white mg-b-1 d-block" htmlFor="settings-language">
                  {t(language, "siteLanguage")}
                </label>
                <select
                  id="settings-language"
                  name="language"
                  className={styles["settings__select"]}
                  value={profileForm.language}
                  onChange={updateProfileField}>
                  {LANGUAGE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="link-medium white mg-b-1 d-block" htmlFor="settings-country">
                  {t(language, "contentCountry")}
                </label>
                <select
                  id="settings-country"
                  name="country"
                  className={styles["settings__select"]}
                  value={profileForm.country}
                  onChange={updateProfileField}>
                  {COUNTRY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles["settings__buttons"]}>
              <Button type="button" onClick={applyDetectedPreferences} tertiary={true}>
                {t(language, "useBrowserLocale")}
              </Button>
              <Button type="submit" disabled={isSavingProfile}>
                {t(language, "saveProfile")} {isSavingProfile && <Spinner className="mg-l-1" />}
              </Button>
            </div>
          </form>

          <form className={styles["settings__section"]} onSubmit={submitPassword}>
            <h2 className="display-small white mg-b-2">{t(language, "password")}</h2>

            <Input
              required={true}
              id="settings-current-password"
              label={t(language, "currentPassword")}
              placeholder={t(language, "currentPasswordPlaceholder")}
              type="password"
              control="input"
              autoComplete="current-password"
              value={passwordForm.currentPassword}
              onChange={updatePasswordField}
              name="currentPassword"
            />

            <Input
              required={true}
              id="settings-new-password"
              label={t(language, "newPassword")}
              placeholder={t(language, "newPasswordPlaceholder")}
              type="password"
              control="input"
              autoComplete="new-password"
              value={passwordForm.newPassword}
              onChange={updatePasswordField}
              name="newPassword"
            />

            <Input
              required={true}
              id="settings-confirm-password"
              label={t(language, "confirmNewPassword")}
              placeholder={t(language, "confirmNewPasswordPlaceholder")}
              type="password"
              control="input"
              autoComplete="new-password"
              value={passwordForm.confirmPassword}
              onChange={updatePasswordField}
              name="confirmPassword"
            />

            <div className={styles["settings__buttons"]}>
              <Button type="submit" disabled={isSavingPassword}>
                {t(language, "updatePassword")} {isSavingPassword && <Spinner className="mg-l-1" />}
              </Button>
            </div>
          </form>
        </Fragment>
      )}
    </div>
  );
}

export default Settings;

